import { Button, Checkbox, Paper, Typography } from '@material-ui/core';
import { debounce } from 'lodash-es';
import React from 'react';

import { getSmallSites } from '~/src/dash/api/channels/channels_methods';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { getSite, ISite } from '~/src/dash/api/console-api';
import { cloneSite, createSite } from '~/src/dash/api/console-api';
import { AsyncButtonV2 } from '~/src/dash/components/button';
import { FormItemTypeText, IFormData, IFormItem, RenderForm } from '~/src/dash/components/form';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { SearchDropdownSingle } from '~/src/dash/components/search-dropdown-single';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { isRoku } from '~/src/dash/resellers/utils/importResellerConfig';
import { extractRouteParams, getRoutePath, RouteChannelExisting } from '~/src/dash/router/routes';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { obfuscateSiteName } from '~/src/dash/utils/obfuscate';
import { routeName } from '~/src/dash/utils/routes';
import { hasErrors, IErrors, IValidator, validatorFactory } from '~/src/dash/utils/validator';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const siteForm = ((): IFormItem[] => {
  const formBase: IFormItem[] = [
    { id: 'Name', label: 'Name', render: FormItemTypeText, required: true },
  ];

  if (isRoku()) {
    formBase.push({
      id: 'RokuProviderChannelID',
      label: 'Roku Provider Channel ID',
      required: true,
      render: FormItemTypeText,
    });
  }

  return formBase;
})();

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  data: IFormData;
  showLoadingWheel: boolean;
  count: number;
  snackbarMessage: string;
  snackbarError: boolean;
  selectedSite?: ISmallSite;
  includeInChannelGroup: boolean;
  site?: ISite;
  sites: ISmallSite[];
  cloneSite?: ISite;
  errors: IErrors;
}

const validationSetting: IValidator = {
  Name(v: string): string {
    if (!v) {
      return 'Name cannot be empty';
    } else if (v === 'New Bidder Name') {
      return 'Choose a custom name';
    }
    return '';
  },
};

export const BidderSiteNewView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginTop: theme.spacing.unit * 4,
        marginRight: 'auto',
        marginLeft: 'auto',
        maxWidth: '600px',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      data: {} as IFormData,
      showLoadingWheel: false,
      count: 0,
      snackbarMessage: '',
      snackbarError: false,
      selectedSite: undefined,
      includeInChannelGroup: false,
      site: {} as ISite,
      sites: [] as ISmallSite[],
      cloneSite: undefined,
      errors: {} as IErrors,
      siteList: {} as IMenuList,
    };

    // _updateSiteDebounce is called to create a new site in the DB. Then the user can complete its setup or create
    // another one.
    _updateSiteDebounce = debounce((count: number) => {
      if (count !== this.state.count) {
        return;
      }
      createSite({
        body: Object.assign(
          {
            form: 'prebid',
            publisher_id: this.props.session.publisher.ID,
            name: this.state.data.Name,
            region: 'us-east-1',
          },
          isRoku() ? { roku_provider_channel_id: this.state.data.RokuProviderChannelID } : {},
        ),
      }).then(
        ({ site }: { site: ISite }) => {
          if (count !== this.state.count) {
            return;
          }
          this.setState({
            showLoadingWheel: false,
            site,
          });
          this.getSites();
        },
        (err: Error) => {
          if (count !== this.state.count) {
            return;
          }
          console.error(err);
          this.setState({
            showLoadingWheel: false,
            snackbarError: true,
            snackbarMessage: 'There seems to be an error creating the site: ' + err.toString(),
          });
        },
      );
    }, 200);

    _cloneSite = debounce((count: number) => {
      if (count !== this.state.count) {
        return;
      }

      const selectedSite = this.state.selectedSite || this.getSelectedSite();

      if (!selectedSite) {
        this.setState({
          showLoadingWheel: false,
          snackbarError: true,
          snackbarMessage: 'Please select a site to clone.',
        });
        return;
      }
      cloneSite({
        body: Object.assign(
          {
            form: 'prebid',
            publisher_id: this.props.session.publisher.ID,
            name: this.state.data.Name,
            clonedUUID: selectedSite.UUID,
            includeInChannelGroup: this.state.includeInChannelGroup,
          },
          isRoku() ? { roku_provider_channel_id: this.state.data.RokuProviderChannelID } : {},
        ),
      }).then(
        ({ site }: { site: ISite }) => {
          if (count !== this.state.count) {
            return;
          }
          this.setState({
            showLoadingWheel: false,
            site,
          });
          this.getSites();
        },
        (err: Error) => {
          if (count !== this.state.count) {
            return;
          }
          console.error(err);
          this.setState({
            showLoadingWheel: false,
            snackbarError: true,
            snackbarMessage: 'There seems to be an error creating the site: ' + err.toString(),
          });
        },
      );
    }, 200);

    componentDidMount() {
      this.getSites();
    }

    getSites = async () => {
      const [smallSites, err] = await getSmallSites(this.props.session.publisher.ID, 'uuid');
      if (err) {
        this.setState({
          snackbarError: true,
          snackbarMessage: 'Error loading sites',
        });
        return;
      }
      const sites: ISmallSite[] = smallSites || [];
      const siteList: IMenuList = {};
      sites.forEach((site: ISmallSite) => {
        const title = obfuscateSiteName(
          site,
          this.props.user.user.Username,
          `[${site.ID}] ${site.Name}`,
        );
        siteList[title] = {
          title,
          selected: false,
          ID: site.ID.toString(),
          site,
        };
      });
      this.setState({
        sites,
        siteList,
      });
    };

    onUpdate = (data: IFormData) => {
      this.setState({ data });
    };

    unSelectSite = () => {
      this.setState({
        data: {} as IFormData,
        site: undefined,
      });
    };

    // updateSite is called when the user click the "Save" button on the "Create new app / site" panel
    updateSite = () => {
      // validate input
      const errors = validatorFactory(validationSetting)(this.state.data);
      if (hasErrors(errors)) {
        this.setState(
          {
            errors,
            snackbarMessage: 'Please fix errors before saving site',
            snackbarError: true,
          },
          () => {
            setTimeout(this.snackbarClose, 6000);
          },
        );
        return;
      }

      const count = this.state.count + 1;
      this.setState({
        showLoadingWheel: true,
        count,
      });
      if (this.state.cloneSite) {
        this._cloneSite(count);
      } else {
        this._updateSiteDebounce(count);
      }
    };

    handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      this.updateSite();
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    refreshModels(site: ISmallSite) {
      this.setState({
        loading: true,
      });
      getSite({
        query: {
          publisher_id: this.props.session.publisher.ID,
          site_id: site.ID,
        },
      }).then(
        (site: ISite) => {
          this.setState({
            Timeout: site.HeaderBidderEndpointTimeout,
            Debug: site.Debug,
            loading: false,
          });
        },
        () => {
          this.setState({
            Timeout: 0,
            loading: false,
          });
        },
      );
    }

    selectSite = (site: ISmallSite) => {
      this.setState({ selectedSite: site }, () => this.refreshModels(site));
    };

    // Handle change of state of the 'Clone an existing website' checkbox
    cloneCheckboxChange = (event: any) => {
      this.setState({ cloneSite: event.target.checked });
    };

    // Handle change of the 'Include the new channel in the same channel group.' checkbox.
    includeInChannelGroupChange = (event: any) => {
      this.setState({ includeInChannelGroup: event.target.checked });
    };

    getSelectedSite = (): ISmallSite | undefined => {
      const selectedSite = this.state.selectedSite;

      if (selectedSite !== undefined) {
        return selectedSite;
      }
      return this.state.sites[0];
    };

    IErrorToFormMap(errors: IErrors): { [key: string]: string[] } {
      const out = {} as { [key: string]: string[] };

      const errorsMap = new Map(Object.entries(errors));

      for (const [key, value] of errorsMap) {
        out[key] = [value];
      }

      return out;
    }

    handleSiteAcceptClick = (): void => {
      for (const { selected, site } of Object.values(this.state.siteList)) {
        if (selected) {
          this.selectSite(site || this.state.sites[0]);
          break;
        }
      }
    };

    setStateCallback = (keys: string[], values: any[], setStateCallbacks: Array<() => void>) => {
      const stateObj = {} as IState;
      for (let i = 0; i < keys.length; i++) {
        stateObj[keys[i]] = values[i];
      }
      this.setState(stateObj, () => {
        setStateCallbacks.forEach((callback: () => void) => callback());
      });
    };

    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      const { siteList } = this.state;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largePadding>
            {'Name' in (this.state.site || {}) ? (
              <Paper
                classes={{
                  root: classes.paper,
                }}
              >
                {/* success panel, displayed after the user create a new site */}
                <Typography variant='h6' style={{ marginBottom: '1rem' }}>
                  Success!
                </Typography>
                <Typography variant='body2' style={{ marginBottom: '2rem' }}>
                  The site `{this.state.site.Name !== undefined ? this.state.site.Name : ''}` was
                  created!
                </Typography>

                {/* "complete setup" button */}
                <Button
                  variant='contained'
                  color='primary'
                  href={
                    '#' +
                    getRoutePath(
                      RouteChannelExisting,
                      extractRouteParams({
                        session: { ...session, site: this.state.site },
                        user,
                      }),
                    )
                  }
                >
                  Complete Setup
                </Button>

                {/* "create another channel" button */}
                <Button
                  style={{ marginLeft: '10px' }}
                  variant='contained'
                  color='primary'
                  onClick={this.unSelectSite}
                >
                  Create Another Channel
                </Button>
              </Paper>
            ) : (
              <Paper
                classes={{
                  root: classes.paper,
                }}
              >
                {/* create new app / site panel */}
                <Typography variant='h6' gutterBottom>
                  Create New App / Site
                </Typography>
                <form onSubmit={this.handleSubmit}>
                  {/* Clone site option */}
                  {this.state.sites.length > 0 && (
                    <div>
                      <Checkbox
                        name='clone'
                        checked={this.state.cloneSite}
                        onChange={this.cloneCheckboxChange}
                      />
                      Clone an existing App / Site
                      {this.state.cloneSite && (
                        <div>
                          <SearchDropdownSingle
                            menuTitle='Channels'
                            list={siteList}
                            onChange={this.setStateCallback}
                            stateKey='siteList'
                            menuAcceptClickCallbacks={[]}
                            handleMenuAcceptClick={this.handleSiteAcceptClick}
                          />
                          <br />
                          <Checkbox
                            name='includeInChannelGroup'
                            checked={this.state.includeInChannelGroup}
                            onChange={this.includeInChannelGroupChange}
                          />
                          Include the new channel in the same channel group.
                        </div>
                      )}
                    </div>
                  )}

                  {/* render the "Name" field */}
                  <RenderForm
                    errors={this.IErrorToFormMap(this.state.errors)}
                    schema={siteForm}
                    data={this.state.data}
                    onUpdate={this.onUpdate}
                  />
                </form>

                {/* render the "Save" button */}
                <div style={{ marginTop: '2rem' }}>
                  <AsyncButtonV2
                    text='Save'
                    inprogress={this.state.showLoadingWheel}
                    onClick={this.updateSite}
                  />
                </div>
              </Paper>
            )}
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={!!this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
